// src/features/payment/AddPaymentMethodModal.tsx
// Tokenizes and attaches a payment method to a billing account.
// CreatePaymentMethod — the token itself comes from a provider-side tokenization
// step (card element, wallet SDK, ...); this form accepts the resulting token,
// as an ops/admin tool would when reconciling or manually attaching a method.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { errMessage } from './format';

const TYPE_OPTIONS = [
  { value: 'PAYMENT_METHOD_TYPE_CARD', labelKey: 'payment.methodType.card' },
  { value: 'PAYMENT_METHOD_TYPE_BANK_ACCOUNT', labelKey: 'payment.methodType.bankAccount' },
  { value: 'PAYMENT_METHOD_TYPE_WALLET', labelKey: 'payment.methodType.wallet' },
  { value: 'PAYMENT_METHOD_TYPE_APM', labelKey: 'payment.methodType.apm' },
  { value: 'PAYMENT_METHOD_TYPE_CRYPTO', labelKey: 'payment.methodType.crypto' },
];

export interface AddPaymentMethodModalProps {
  open: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (data: {
    type: string;
    provider_code: string;
    provider_token: string;
    set_as_default: boolean;
  }) => Promise<void>;
}

export function AddPaymentMethodModal({ open, isSaving, onCancel, onSubmit }: AddPaymentMethodModalProps) {
  const { t } = useI18n();
  const [type, setType] = useState('PAYMENT_METHOD_TYPE_CARD');
  const [providerCode, setProviderCode] = useState('');
  const [providerToken, setProviderToken] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const providerCodeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setType('PAYMENT_METHOD_TYPE_CARD');
    setProviderCode('');
    setProviderToken('');
    setSetAsDefault(false);
    setError(null);
  }, [open]);

  const canSubmit = !!providerCode.trim() && !!providerToken.trim();
  const dirty = !!(providerCode.trim() || providerToken.trim() || setAsDefault || type !== 'PAYMENT_METHOD_TYPE_CARD');

  const submit = async () => {
    if (!canSubmit || isSaving) return;
    setError(null);
    try {
      await onSubmit({ type, provider_code: providerCode.trim(), provider_token: providerToken.trim(), set_as_default: setAsDefault });
    } catch (err) {
      setError(errMessage(err, t('payment.methods.addError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onCancel}
      onSubmit={submit}
      submitLabel={t('common.add')}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      aria-label={t('payment.methods.add')}
    >
      <div className="space-y-1 px-3.5 pb-4 pt-3">
        {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

        <FormRow label={t('payment.methods.type')} labelWidth="sm">
          <Select
            value={type}
            onChange={(e) => setType(e.target.value)}
            fullWidth={false}
            className="w-full"
            aria-label={t('payment.methods.type')}
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
            ))}
          </Select>
        </FormRow>
        <FormRow label={t('payment.methods.providerCode')} labelWidth="sm">
          <input
            ref={providerCodeRef}
            data-autofocus
            value={providerCode}
            onChange={(e) => setProviderCode(e.target.value)}
            placeholder="stripe"
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('payment.methods.providerToken')} labelWidth="sm">
          <input
            value={providerToken}
            onChange={(e) => setProviderToken(e.target.value)}
            placeholder="tok_xxx"
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>

        <div className="pt-2">
          <ToggleSwitch label={t('payment.methods.setAsDefault')} checked={setAsDefault} onChange={setSetAsDefault} />
        </div>
      </div>
    </FormDialog>
  );
}

export default AddPaymentMethodModal;
